import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { CommentAttachment, CommentData } from "@liveblocks/core";
import {
  useAttachmentUrl,
  useSelf,
  useDeleteComment,
  useEditComment,
  useAddReaction,
  useRemoveReaction,
} from "@liveblocks/react";
import { CommentBodyRenderer } from "./CommentBodyRenderer";
import { CommentComposer } from "./CommentComposer";
import EmojiPicker from "./EmojiPicker";
import { useLiveblocksUser } from "./useLiveblocksUser";
import { formatRelative } from "./utils";

type Props = {
  threadId: string;
  comment: CommentData;
};

/**
 * Full-screen lightbox for a comment image attachment. Renders a portal so
 * it escapes the comment popover's clip/overflow context, dims the rest of
 * the builder, and dismisses on backdrop click or Escape — Figma-style.
 *
 * Kept local to the CommentItem module since it's tightly coupled to the
 * thumbnail's open state and only relevant to comments. If we ever need a
 * generic builder lightbox elsewhere, lift this to a shared module.
 */
const ImageLightbox: React.FC<{
  url: string;
  name: string;
  onClose: () => void;
}> = ({ url, name, onClose }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    // Prevent the page underneath from scrolling while the overlay is open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return createPortal(
    <div
      // z-index above the comments popover (z-50 / z-[2147483647] used for
      // mention dropdown). Use the max safe int to guarantee top-of-stack.
      className="fixed inset-0 z-[2147483647] flex items-center justify-center p-8"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.75)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" }}
      data-comment-portal
      onClick={onClose}
      onMouseDown={(e) => e.stopPropagation()}
      role="dialog"
      aria-label={name}
      aria-modal="true"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-[18px] font-medium leading-none text-white transition hover:bg-white/25"
      >
        ×
      </button>
      <img
        src={url}
        alt={name}
        // Stop click on the image itself from bubbling to the backdrop and
        // closing the overlay — only background clicks dismiss.
        onClick={(e) => e.stopPropagation()}
        className="max-h-full max-w-full rounded-md object-contain shadow-[0_24px_64px_rgba(0,0,0,0.4)]"
        draggable={false}
      />
    </div>,
    document.body
  );
};

/**
 * Renders a single attached file as a thumbnail. For images, fetches a
 * presigned URL via `useAttachmentUrl` and shows it inline; clicking opens
 * a Figma-style lightbox overlay (see `ImageLightbox`). Non-image MIME types
 * (we currently only upload images, but old threads may have other types)
 * fall back to a name chip with a download link.
 */
const CommentAttachmentThumb: React.FC<{ attachment: CommentAttachment }> = ({ attachment }) => {
  const { url, isLoading, error } = useAttachmentUrl(attachment.id);
  const isImage = attachment.mimeType?.startsWith("image/");
  const [isOpen, setIsOpen] = useState(false);

  if (!isImage) {
    return (
      <a
        href={url || "#"}
        target="_blank"
        rel="noopener noreferrer"
        className="flex max-w-[200px] items-center gap-1.5 rounded-md border border-solid border-[#e2e2fc] bg-[#fafafd] px-2 py-1 text-[11px] text-[#130f26] hover:bg-[#f4f4f7]"
        title={attachment.name}
      >
        <span className="truncate">{attachment.name}</span>
      </a>
    );
  }

  if (isLoading) {
    return <div className="h-[120px] w-[120px] animate-pulse rounded-md bg-[#f4f4f7]" />;
  }
  if (error || !url) {
    return (
      <div className="flex h-[120px] w-[120px] items-center justify-center rounded-md border border-solid border-[#e2e2fc] bg-[#fafafd] text-[10px] text-[#130f26]/50">
        Failed to load
      </div>
    );
  }
  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        title={attachment.name}
        aria-label={`Open ${attachment.name}`}
        className="block cursor-zoom-in overflow-hidden rounded-md border border-solid border-[#e2e2fc] bg-transparent p-0"
      >
        <img
          src={url}
          alt={attachment.name}
          className="block max-h-[160px] max-w-[200px] object-cover transition hover:opacity-90"
          draggable={false}
        />
      </button>
      {isOpen && <ImageLightbox url={url} name={attachment.name} onClose={() => setIsOpen(false)} />}
    </>
  );
};

const Avatar = ({ userId, size = 28 }: { userId: string; size?: number }) => {
  const user = useLiveblocksUser(userId);
  const initial = (user.name || "?").charAt(0).toUpperCase();
  const bg = user.color || "#4f5b94";
  if (user.avatar) {
    return (
      <img
        src={user.avatar}
        alt={user.name || "user"}
        width={size}
        height={size}
        className="rounded-full object-cover"
        draggable={false}
      />
    );
  }
  return (
    <div
      className="flex items-center justify-center rounded-full text-white text-[11px] font-semibold uppercase"
      style={{ width: size, height: size, backgroundColor: bg }}
    >
      {initial}
    </div>
  );
};

export const CommentItem: React.FC<Props> = ({ threadId, comment }) => {
  const user = useLiveblocksUser(comment.userId);
  const self = useSelf();
  const isAuthor = self?.id === comment.userId;
  const authorDisplayName = user.name;
  const editComment = useEditComment();
  const deleteComment = useDeleteComment();
  const addReaction = useAddReaction();
  const removeReaction = useRemoveReaction();
  const [isEditing, setIsEditing] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isQuickOpen, setIsQuickOpen] = useState(false);
  const pickerWrapRef = useRef<HTMLDivElement>(null);
  const smileBtnRef = useRef<HTMLButtonElement>(null);
  const pickerPopoverRef = useRef<HTMLDivElement>(null);
  const quickPopoverRef = useRef<HTMLDivElement>(null);
  const [pickerPos, setPickerPos] = useState<{ top: number; left: number } | null>(null);
  const [quickPos, setQuickPos] = useState<{ top: number; left: number } | null>(null);

  const QUICK_REACTIONS = ["👍", "❤️", "😂", "🎉", "😮", "🙏"];
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const menuPopoverRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!isPickerOpen) {
      setPickerPos(null);
      return;
    }
    const btn = smileBtnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const PICKER_W = 280;
    const PICKER_H = 300;
    let left = rect.right - PICKER_W;
    if (left < 8) left = 8;
    if (left + PICKER_W > window.innerWidth - 8) left = window.innerWidth - PICKER_W - 8;
    let top = rect.bottom + 4;
    if (top + PICKER_H > window.innerHeight - 8) top = rect.top - PICKER_H - 4;
    if (top < 8) top = 8;
    setPickerPos({ top, left });
  }, [isPickerOpen]);

  useEffect(() => {
    if (!isPickerOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (pickerWrapRef.current?.contains(target)) return;
      if (pickerPopoverRef.current?.contains(target)) return;
      setIsPickerOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isPickerOpen]);

  useLayoutEffect(() => {
    if (!showMenu) {
      setMenuPos(null);
      return;
    }
    const btn = menuBtnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const MENU_W = 120;
    const MENU_H = 72;
    let left = rect.right - MENU_W;
    if (left < 8) left = 8;
    if (left + MENU_W > window.innerWidth - 8) left = window.innerWidth - MENU_W - 8;
    let top = rect.bottom + 4;
    if (top + MENU_H > window.innerHeight - 8) top = rect.top - MENU_H - 4;
    if (top < 8) top = 8;
    setMenuPos({ top, left });
  }, [showMenu]);

  useEffect(() => {
    if (!showMenu) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuBtnRef.current?.contains(target)) return;
      if (menuPopoverRef.current?.contains(target)) return;
      setShowMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showMenu]);

  useLayoutEffect(() => {
    if (!isQuickOpen) {
      setQuickPos(null);
      return;
    }
    const btn = smileBtnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const QUICK_W = 240;
    const QUICK_H = 44;
    let left = rect.right - QUICK_W;
    if (left < 8) left = 8;
    if (left + QUICK_W > window.innerWidth - 8) left = window.innerWidth - QUICK_W - 8;
    let top = rect.top - QUICK_H - 6;
    if (top < 8) top = rect.bottom + 6;
    setQuickPos({ top, left });
  }, [isQuickOpen]);

  useEffect(() => {
    if (!isQuickOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (pickerWrapRef.current?.contains(target)) return;
      if (quickPopoverRef.current?.contains(target)) return;
      setIsQuickOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isQuickOpen]);

  const handleToggleReaction = (emoji: string) => {
    if (!self?.id) return;
    const existing = comment.reactions.find((r) => r.emoji === emoji);
    const hasMine = !!existing?.users.some((u) => u.id === self.id);
    if (hasMine) {
      removeReaction({ threadId, commentId: comment.id, emoji });
    } else {
      addReaction({ threadId, commentId: comment.id, emoji });
    }
  };

  if (!comment.body) {
    return <div className="flex gap-2 py-2 text-[12px] italic text-[#130f26]/40">(deleted)</div>;
  }

  return (
    <div className="group relative flex gap-2 py-2">
      <div className="shrink-0 pt-0.5">
        <Avatar userId={comment.userId} />
      </div>
      <div className="min-w-0 flex-1 pr-[30px]">
        <div className="flex items-baseline gap-2">
          <span className="truncate text-[13px] font-semibold text-[#130f26]">{authorDisplayName}</span>
          <span className="text-[11px] text-[#130f26]/50">
            {formatRelative(comment.createdAt)}
            {comment.editedAt ? " · edited" : ""}
          </span>
          {!isEditing && (
            <div
              className={`absolute right-0 top-2 flex gap-0.5 opacity-0 transition group-hover:opacity-100 group-focus-within:opacity-100 ${
                isAuthor ? "flex-col items-end" : "items-center"
              }`}
            >
              {isAuthor && (
                <div className="relative">
                  <button
                    ref={menuBtnRef}
                    type="button"
                    onClick={() => setShowMenu((v) => !v)}
                    className="rounded p-1 text-[#130f26]/50 hover:bg-[#f4f4f7] hover:text-[#130f26]"
                    aria-label="Comment actions"
                  >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                      <circle cx="3" cy="8" r="1.5" />
                      <circle cx="8" cy="8" r="1.5" />
                      <circle cx="13" cy="8" r="1.5" />
                    </svg>
                  </button>
                  {showMenu &&
                    menuPos &&
                    createPortal(
                      <div
                        ref={menuPopoverRef}
                        data-comment-portal
                        style={{
                          position: "fixed",
                          top: menuPos.top,
                          left: menuPos.left,
                          zIndex: 2147483647,
                        }}
                        className="min-w-[120px] overflow-hidden rounded-md border border-[#e2e2fc] bg-white py-1 shadow-lg"
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setIsEditing(true);
                            setShowMenu(false);
                          }}
                          className="block w-full px-3 py-1.5 text-left text-[12px] text-[#130f26] hover:bg-[#f4f4f7]"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            deleteComment({ threadId, commentId: comment.id });
                            setShowMenu(false);
                          }}
                          className="block w-full px-3 py-1.5 text-left text-[12px] text-[#d93025] hover:bg-[#fde7e7]"
                        >
                          Delete
                        </button>
                      </div>,
                      document.body
                    )}
                </div>
              )}
              <div ref={pickerWrapRef} className="relative">
                <button
                  ref={smileBtnRef}
                  type="button"
                  onClick={() => {
                    setIsQuickOpen((v) => !v);
                    setIsPickerOpen(false);
                  }}
                  className="rounded p-1 text-[#130f26]/50 hover:bg-[#f4f4f7] hover:text-[#130f26]"
                  aria-label="Add reaction"
                  title="Add reaction"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="8" cy="8" r="6.5" />
                    <circle cx="6" cy="7" r="0.75" fill="currentColor" />
                    <circle cx="10" cy="7" r="0.75" fill="currentColor" />
                    <path d="M5.5 10c.6.8 1.5 1.25 2.5 1.25s1.9-.45 2.5-1.25" strokeLinecap="round" />
                  </svg>
                </button>
                {isQuickOpen &&
                  quickPos &&
                  createPortal(
                    <div
                      ref={quickPopoverRef}
                      data-comment-portal
                      style={{
                        position: "fixed",
                        top: quickPos.top,
                        left: quickPos.left,
                        zIndex: 2147483647,
                      }}
                      className="flex items-center gap-0.5 rounded-lg border border-solid border-[#e2e2fc] bg-white p-1 shadow-[0_8px_24px_rgba(16,24,40,0.12)]"
                    >
                      {QUICK_REACTIONS.map((emoji) => {
                        const existing = comment.reactions.find((r) => r.emoji === emoji);
                        const mine = !!self?.id && existing?.users.some((u) => u.id === self.id);
                        return (
                          <button
                            key={emoji}
                            type="button"
                            onClick={() => {
                              handleToggleReaction(emoji);
                              setIsQuickOpen(false);
                            }}
                            className={`flex h-8 w-8 items-center justify-center rounded text-[18px] leading-none transition hover:scale-110 ${
                              mine ? "bg-[#e2e2fc]" : "hover:bg-[#f4f4f7]"
                            }`}
                            title={mine ? "Remove reaction" : `React with ${emoji}`}
                          >
                            {emoji}
                          </button>
                        );
                      })}
                      <div className="mx-1 h-5 w-px bg-[#e2e2fc]" />
                      <button
                        type="button"
                        onClick={() => {
                          setIsQuickOpen(false);
                          setIsPickerOpen(true);
                        }}
                        className="flex h-8 w-8 items-center justify-center rounded text-[#130f26]/50 hover:bg-[#f4f4f7] hover:text-[#130f26]"
                        title="More emojis"
                        aria-label="More emojis"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        >
                          <circle cx="8" cy="8" r="6.5" />
                          <path d="M5.5 10c.6.8 1.5 1.25 2.5 1.25s1.9-.45 2.5-1.25" strokeLinecap="round" />
                          <path d="M5.5 5v2M4.5 6h2" strokeLinecap="round" />
                          <path d="M10.5 5v2M9.5 6h2" strokeLinecap="round" />
                        </svg>
                      </button>
                    </div>,
                    document.body
                  )}
                {isPickerOpen &&
                  pickerPos &&
                  createPortal(
                    <div
                      ref={pickerPopoverRef}
                      data-comment-portal
                      style={{ position: "fixed", top: pickerPos.top, left: pickerPos.left, zIndex: 2147483647 }}
                    >
                      <EmojiPicker
                        onPick={(emoji) => {
                          handleToggleReaction(emoji);
                          setIsPickerOpen(false);
                        }}
                        onClose={() => setIsPickerOpen(false)}
                      />
                    </div>,
                    document.body
                  )}
              </div>
            </div>
          )}
        </div>
        <div className="mt-0.5">
          {isEditing ? (
            <CommentComposer
              compact
              autoFocus
              submitLabel="Save"
              disableAttachments
              initialBody={comment.body}
              onSubmit={async (body) => {
                await editComment({ threadId, commentId: comment.id, body });
                setIsEditing(false);
              }}
              onCancel={() => setIsEditing(false)}
            />
          ) : (
            <CommentBodyRenderer body={comment.body} />
          )}
        </div>
        {!isEditing && comment.attachments && comment.attachments.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {comment.attachments.map((a) => (
              <CommentAttachmentThumb key={a.id} attachment={a} />
            ))}
          </div>
        )}
        {comment.reactions.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {comment.reactions.map((r) => {
              const mine = !!self?.id && r.users.some((u) => u.id === self.id);
              return (
                <button
                  key={r.emoji}
                  type="button"
                  onClick={() => handleToggleReaction(r.emoji)}
                  className={`flex h-7 items-center gap-1 rounded border border-solid px-3 transition hover:border-[#dadaf2] hover:bg-[#f1f1f7] ${
                    mine ? "border-[#dadaf2] bg-[#fafafd]" : "border-[#e2e2fc] bg-[#fafafd]"
                  }`}
                  title={`${r.users.length} ${r.users.length === 1 ? "reaction" : "reactions"}`}
                >
                  <span className="text-[14px] leading-none">{r.emoji}</span>
                  {r.users.length > 1 && (
                    <span
                      className={`text-[11px] font-semibold leading-none ${
                        mine ? "text-[#4f5b94]" : "text-[#130f26]/70"
                      }`}
                    >
                      {r.users.length}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export { Avatar };
